import React, { useEffect, useMemo, useState } from 'react';
import { adminAPI, getMediaUrl, normalizeGalleryItem } from '../../services/api.jsx';
import { getAdminGalleryFolders } from '../../services/api.jsx';
import { useAuth } from '../../contexts/AuthContext.jsx';

const LANGS = ['de', 'en', 'it'];

const emptyMultilingual = () => ({ de: '', en: '', it: '' });

const getPrimaryText = (bundle) => {
    if (!bundle) return '';
    const order = ['de', 'en', 'it'];
    for (const key of order) {
        const value = bundle[key];
        if (value && value.trim()) {
            return value.trim();
        }
    }
    return '';
};

const CollageManager = () => {
    const { isAuthenticated } = useAuth();

    const [collages, setCollages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [folders, setFolders] = useState([]);

    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [createLang, setCreateLang] = useState('de');
    const [createError, setCreateError] = useState('');
    const [creating, setCreating] = useState(false);
    const [newCollage, setNewCollage] = useState({
        titles: emptyMultilingual(),
        descriptions: emptyMultilingual(),
        folderId: '',
        files: [],
    });

    const [manageModalOpen, setManageModalOpen] = useState(false);
    const [manageLang, setManageLang] = useState('de');
    const [manageError, setManageError] = useState('');
    const [savingChanges, setSavingChanges] = useState(false);
    const [imageBusy, setImageBusy] = useState(false);
    const [deletingCollage, setDeletingCollage] = useState(false);
    const [editingCollage, setEditingCollage] = useState(null);
    const [initialEditingCollage, setInitialEditingCollage] = useState(null);

    useEffect(() => {
        fetchCollages();
    }, []);

    useEffect(() => {
        if (isAuthenticated) {
            fetchFolders();
        }
    }, [isAuthenticated]);

    const fetchCollages = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await adminAPI.getCollages();
            const data = Array.isArray(response.data) ? response.data.map(normalizeGalleryItem) : [];
            setCollages(data);
        } catch (err) {
            console.error('Error fetching collages:', err);
            setError('Fehler beim Laden der Collagen');
        } finally {
            setLoading(false);
        }
    };

    const fetchFolders = async () => {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) return;
            const data = await getAdminGalleryFolders(token);
            setFolders(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error fetching folders for collages:', err);
        }
    };

    const resetCreateState = () => {
        setNewCollage({
            titles: emptyMultilingual(),
            descriptions: emptyMultilingual(),
            folderId: '',
            files: [],
        });
        setCreateLang('de');
        setCreateError('');
    };

    const handleCreateFilesChange = (event) => {
        const fileList = Array.from(event.target.files || []);
        event.target.value = '';
        if (!fileList.length) return;
        setNewCollage((prev) => ({ ...prev, files: fileList }));
    };

    const handleCreateValueChange = (type, lang, value) => {
        setNewCollage((prev) => ({
            ...prev,
            [type]: {
                ...prev[type],
                [lang]: value,
            },
        }));
    };

    const handleCreateCollage = async () => {
        if (!newCollage.files.length) {
            setCreateError('Bitte fügen Sie mindestens ein Bild hinzu.');
            return;
        }

        setCreating(true);
        setCreateError('');
        try {
            const response = await adminAPI.createCollage({
                titles: newCollage.titles,
                descriptions: newCollage.descriptions,
                folderId: newCollage.folderId || null,
                files: newCollage.files,
            });
            const created = normalizeGalleryItem(response.data);
            setCollages((prev) => [created, ...prev]);
            setCreateModalOpen(false);
            resetCreateState();
        } catch (err) {
            console.error('Error creating collage:', err);
            const detail = err.response?.data?.detail;
            setCreateError(detail || 'Fehler beim Erstellen der Collage');
        } finally {
            setCreating(false);
        }
    };

    const openManageModal = (collage) => {
        const normalized = normalizeGalleryItem(collage);
        const titles = {
            de: normalized.title_de || '',
            en: normalized.title_en || '',
            it: normalized.title_it || '',
        };
        const descriptions = {
            de: normalized.description_de || '',
            en: normalized.description_en || '',
            it: normalized.description_it || '',
        };
        const folderId = normalized.folder_id ?? '';
        const images = Array.isArray(normalized.collage_images) ? normalized.collage_images : [];

        setEditingCollage({
            id: normalized.id,
            titles: { ...titles },
            descriptions: { ...descriptions },
            folderId: folderId === null ? '' : folderId,
            images: [...images],
        });
        setInitialEditingCollage({
            id: normalized.id,
            titles: { ...titles },
            descriptions: { ...descriptions },
            folderId: folderId === null ? '' : folderId,
            images: [...images],
        });
        setManageLang('de');
        setManageError('');
        setManageModalOpen(true);
    };

    const closeManageModal = () => {
        setManageModalOpen(false);
        setEditingCollage(null);
        setInitialEditingCollage(null);
        setManageError('');
    };

    const updateEditingValue = (type, lang, value) => {
        setEditingCollage((prev) => ({
            ...prev,
            [type]: {
                ...prev[type],
                [lang]: value,
            },
        }));
    };

    const handleManageFolderChange = (value) => {
        setEditingCollage((prev) => ({
            ...prev,
            folderId: value,
        }));
    };

    const moveImage = (index, direction) => {
        setEditingCollage((prev) => {
            if (!prev) return prev;
            const images = [...(prev.images || [])];
            const newIndex = index + direction;
            if (newIndex < 0 || newIndex >= images.length) return prev;
            const [item] = images.splice(index, 1);
            images.splice(newIndex, 0, item);
            return { ...prev, images };
        });
    };

    const handleDeleteCollageImage = async (collageId, imageId) => {
        if (!window.confirm('Möchten Sie dieses Bild wirklich aus der Collage entfernen?')) {
            return;
        }
        setImageBusy(true);
        setManageError('');
        try {
            const response = await adminAPI.deleteCollageImage(collageId, imageId);
            const updated = normalizeGalleryItem(response.data);
            setCollages((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
            setEditingCollage((prev) => ({
                ...prev,
                images: Array.isArray(updated.collage_images) ? [...updated.collage_images] : [],
            }));
            setInitialEditingCollage((prev) => ({
                ...prev,
                images: Array.isArray(updated.collage_images) ? [...updated.collage_images] : [],
            }));
        } catch (err) {
            console.error('Error deleting collage image:', err);
            const detail = err.response?.data?.detail;
            setManageError(detail || 'Fehler beim Entfernen des Bildes');
        } finally {
            setImageBusy(false);
        }
    };

    const handleAddImages = async (event) => {
        if (!editingCollage) return;
        const fileList = Array.from(event.target.files || []);
        event.target.value = '';
        if (!fileList.length) return;
        setImageBusy(true);
        setManageError('');
        try {
            const response = await adminAPI.addCollageImages(editingCollage.id, fileList);
            const updated = normalizeGalleryItem(response.data);
            setCollages((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
            setEditingCollage({
                ...editingCollage,
                images: Array.isArray(updated.collage_images) ? [...updated.collage_images] : [],
            });
            setInitialEditingCollage((prev) => ({
                ...prev,
                images: Array.isArray(updated.collage_images) ? [...updated.collage_images] : [],
            }));
        } catch (err) {
            console.error('Error adding collage images:', err);
            const detail = err.response?.data?.detail;
            setManageError(detail || 'Fehler beim Hinzufügen der Bilder');
        } finally {
            setImageBusy(false);
        }
    };

    const handleSaveCollage = async () => {
        if (!editingCollage || !initialEditingCollage) {
            return;
        }

        const titleUpdates = {};
        const descriptionUpdates = {};
        let hasMetadataChanges = false;
        LANGS.forEach((lang) => {
            const trimmedTitle = editingCollage.titles[lang];
            const trimmedDesc = editingCollage.descriptions[lang];
            if (trimmedTitle !== initialEditingCollage.titles[lang]) {
                titleUpdates[lang] = trimmedTitle;
                hasMetadataChanges = true;
            }
            if (trimmedDesc !== initialEditingCollage.descriptions[lang]) {
                descriptionUpdates[lang] = trimmedDesc;
                hasMetadataChanges = true;
            }
        });

        let folderChanged = false;
        if (editingCollage.folderId !== initialEditingCollage.folderId) {
            folderChanged = true;
        }

        const initialOrder = (initialEditingCollage.images || []).map((img) => img.id);
        const currentOrder = (editingCollage.images || []).map((img) => img.id);
        const orderChanged = initialOrder.length === currentOrder.length
            ? currentOrder.some((id, idx) => id !== initialOrder[idx])
            : true;

        if (!hasMetadataChanges && !folderChanged && !orderChanged) {
            setManageModalOpen(false);
            return;
        }

        const payload = {};
        if (hasMetadataChanges) {
            if (Object.keys(titleUpdates).length > 0) payload.titles = titleUpdates;
            if (Object.keys(descriptionUpdates).length > 0) payload.descriptions = descriptionUpdates;
        }
        if (folderChanged) {
            const folderValue = editingCollage.folderId === '' ? null : editingCollage.folderId;
            payload.folderId = folderValue;
        }
        if (orderChanged) {
            payload.imageOrder = currentOrder;
        }

        setSavingChanges(true);
        setManageError('');
        try {
            const response = await adminAPI.updateCollage(editingCollage.id, payload);
            const updated = normalizeGalleryItem(response.data);
            setCollages((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
            setEditingCollage({
                id: updated.id,
                titles: {
                    de: updated.title_de || '',
                    en: updated.title_en || '',
                    it: updated.title_it || '',
                },
                descriptions: {
                    de: updated.description_de || '',
                    en: updated.description_en || '',
                    it: updated.description_it || '',
                },
                folderId: updated.folder_id ?? '',
                images: Array.isArray(updated.collage_images) ? [...updated.collage_images] : [],
            });
            setInitialEditingCollage({
                id: updated.id,
                titles: {
                    de: updated.title_de || '',
                    en: updated.title_en || '',
                    it: updated.title_it || '',
                },
                descriptions: {
                    de: updated.description_de || '',
                    en: updated.description_en || '',
                    it: updated.description_it || '',
                },
                folderId: updated.folder_id ?? '',
                images: Array.isArray(updated.collage_images) ? [...updated.collage_images] : [],
            });
            setManageModalOpen(false);
        } catch (err) {
            console.error('Error saving collage:', err);
            const detail = err.response?.data?.detail;
            setManageError(detail || 'Fehler beim Speichern der Collage');
        } finally {
            setSavingChanges(false);
        }
    };

    const handleDeleteCollage = async (collageId) => {
        if (!window.confirm('Möchten Sie diese Collage wirklich löschen?')) {
            return;
        }
        setDeletingCollage(true);
        setError('');
        try {
            await adminAPI.deleteCollage(collageId);
            setCollages((prev) => prev.filter((item) => item.id !== collageId));
            if (editingCollage?.id === collageId) {
                closeManageModal();
            }
        } catch (err) {
            console.error('Error deleting collage:', err);
            const detail = err.response?.data?.detail;
            setError(detail || 'Fehler beim Löschen der Collage');
        } finally {
            setDeletingCollage(false);
        }
    };

    const collageCards = useMemo(() => collages.map(normalizeGalleryItem), [collages]);

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-neutral-950">Collagen verwalten</h2>
                <div className="flex items-center space-x-3">
                    <button
                        onClick={() => {
                            resetCreateState();
                            setCreateModalOpen(true);
                        }}
                        className="inline-flex items-center px-4 py-2 rounded-md bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors"
                    >
                        <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 5v14" />
                            <path d="M5 12h14" />
                        </svg>
                        Neue Collage
                    </button>
                    <button
                        onClick={fetchCollages}
                        className="inline-flex items-center px-4 py-2 rounded-md bg-neutral-600 hover:bg-neutral-700 text-white text-sm font-medium transition-colors"
                    >
                        Aktualisieren
                    </button>
                </div>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
            ) : (
                <>
                    {collageCards.length === 0 ? (
                        <div className="text-center py-12">
                            <div className="text-neutral-500">
                                <svg className="w-16 h-16 mx-auto mb-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                    <path d="M21 15l-5-5L5 21" />
                                </svg>
                                <p>Keine Collagen vorhanden</p>
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                            {collageCards.map((collage) => {
                                const coverUrl = getMediaUrl(collage.filename);
                                const title = getPrimaryText({
                                    de: collage.title_de,
                                    en: collage.title_en,
                                    it: collage.title_it,
                                }) || 'Ohne Titel';
                                const description = getPrimaryText({
                                    de: collage.description_de,
                                    en: collage.description_en,
                                    it: collage.description_it,
                                });
                                const imageCount = collage.collage_images ? collage.collage_images.length : 0;
                                return (
                                    <div key={collage.id} className="bg-white rounded-xl border border-neutral-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                                        <div className="relative h-52 bg-neutral-100 overflow-hidden">
                                            {coverUrl ? (
                                                <img
                                                    src={coverUrl}
                                                    alt={title}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-neutral-400">
                                                    Kein Titelbild
                                                </div>
                                            )}
                                            <div className="absolute top-3 left-3 px-2 py-1 rounded-md bg-primary-600 text-white text-xs font-semibold uppercase">
                                                Collage
                                            </div>
                                            <div className="absolute top-3 right-3 px-2 py-1 rounded-md bg-black/60 text-white text-xs font-medium">
                                                {imageCount} Bild{imageCount === 1 ? '' : 'er'}
                                            </div>
                                        </div>
                                        <div className="p-4 space-y-3">
                                            <div>
                                                <h3 className="text-lg font-semibold text-neutral-900">{title}</h3>
                                                {description && (
                                                    <p className="text-sm text-neutral-600 line-clamp-2">{description}</p>
                                                )}
                                            </div>
                                            <div className="text-xs text-neutral-500">
                                                Ordner: {collage.folder_id ? (folders.find((f) => f.id === collage.folder_id)?.description_de || folders.find((f) => f.id === collage.folder_id)?.description_en || folders.find((f) => f.id === collage.folder_id)?.description_it || `#${collage.folder_id}`) : 'Keiner'}
                                            </div>
                                            <div className="flex items-center justify-between pt-3 border-t border-neutral-200">
                                                <button
                                                    onClick={() => openManageModal(collage)}
                                                    className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700"
                                                >
                                                    <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M11 4h2M4 11v2M11 20h2M20 11v2" />
                                                    </svg>
                                                    Bearbeiten
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteCollage(collage.id)}
                                                    className="inline-flex items-center text-sm text-red-600 hover:text-red-700"
                                                    disabled={deletingCollage}
                                                >
                                                    <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M19 7l-.867 12.142A2 2 0 0 1 16.138 21H7.862a2 2 0 0 1-1.995-1.858L5 7" />
                                                        <path d="M10 11v6" />
                                                        <path d="M14 11v6" />
                                                        <path d="M15.5 4l-1-1h-5l-1 1H5v2h14V4z" />
                                                    </svg>
                                                    Löschen
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </>
            )}

            {createModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between px-6 py-4 border-b">
                            <h3 className="text-lg font-semibold">Neue Collage erstellen</h3>
                            <button onClick={() => { setCreateModalOpen(false); resetCreateState(); }} className="text-neutral-500 hover:text-neutral-700">
                                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M18 6L6 18M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
                            {createError && (
                                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-md text-sm">
                                    {createError}
                                </div>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-xs font-medium text-neutral-600 mb-2">Bilder</label>
                                        <div className="border-2 border-dashed border-neutral-300 rounded-xl p-6 text-center bg-neutral-50/70">
                                            {newCollage.files.length > 0 ? (
                                                <div className="space-y-2 text-sm text-neutral-600">
                                                    <p className="font-medium">{newCollage.files.length} Dateien ausgewählt</p>
                                                    <ul className="max-h-32 overflow-y-auto text-xs text-neutral-500 space-y-1">
                                                        {newCollage.files.map((file) => (
                                                            <li key={file.name} className="truncate">{file.name}</li>
                                                        ))}
                                                    </ul>
                                                    <button
                                                        onClick={() => setNewCollage((prev) => ({ ...prev, files: [] }))}
                                                        className="text-xs text-primary-600 hover:text-primary-700"
                                                    >
                                                        Auswahl zurücksetzen
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="text-sm text-neutral-500">
                                                    <p className="mb-2">Mehrere Bilder können ausgewählt werden.</p>
                                                    <label className="inline-flex items-center px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-white text-sm font-medium cursor-pointer shadow-sm">
                                                        Dateien auswählen
                                                        <input type="file" accept="image/*" multiple className="hidden" onChange={handleCreateFilesChange} />
                                                    </label>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-neutral-600 mb-2">Ordner</label>
                                        <select
                                            value={newCollage.folderId}
                                            onChange={(e) => setNewCollage((prev) => ({ ...prev, folderId: e.target.value }))}
                                            className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                                        >
                                            <option value="">Kein Ordner</option>
                                            {folders.map((folder) => (
                                                <option key={folder.id} value={folder.id}>
                                                    {folder.description_de || folder.description_en || folder.description_it || `Ordner ${folder.id}`}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <div className="inline-flex rounded-lg border border-neutral-200 bg-neutral-50 overflow-hidden text-sm">
                                        {LANGS.map((lang) => (
                                            <button
                                                key={lang}
                                                type="button"
                                                onClick={() => setCreateLang(lang)}
                                                className={`px-3 py-1.5 font-medium transition-colors ${createLang === lang ? 'bg-white text-neutral-900 shadow-inner' : 'text-neutral-500 hover:text-neutral-800 hover:bg-neutral-100'}`}
                                            >
                                                {lang.toUpperCase()}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-xs font-medium text-neutral-600 mb-1">Titel ({createLang.toUpperCase()})</label>
                                            <input
                                                type="text"
                                                value={newCollage.titles[createLang]}
                                                onChange={(e) => handleCreateValueChange('titles', createLang, e.target.value)}
                                                className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                                                placeholder="Titel der Collage"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-neutral-600 mb-1">Beschreibung ({createLang.toUpperCase()})</label>
                                            <textarea
                                                rows={4}
                                                value={newCollage.descriptions[createLang]}
                                                onChange={(e) => handleCreateValueChange('descriptions', createLang, e.target.value)}
                                                className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                                                placeholder="Beschreibung der Collage"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="border-t bg-neutral-50 px-6 py-4 flex items-center justify-between">
                            <div className="text-xs text-neutral-500">Das erste ausgewählte Bild wird als Titelbild verwendet.</div>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => { setCreateModalOpen(false); resetCreateState(); }}
                                    className="px-4 h-10 rounded-lg bg-neutral-200 hover:bg-neutral-300 text-neutral-800 text-sm font-medium"
                                >
                                    Abbrechen
                                </button>
                                <button
                                    disabled={creating}
                                    onClick={handleCreateCollage}
                                    className="px-5 h-10 rounded-lg bg-primary-600 hover:bg-primary-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium inline-flex items-center gap-2"
                                >
                                    {creating && <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></span>}
                                    Collage erstellen
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {manageModalOpen && editingCollage && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[94vh] overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between px-6 py-4 border-b">
                            <h3 className="text-lg font-semibold">Collage bearbeiten</h3>
                            <button onClick={closeManageModal} className="text-neutral-500 hover:text-neutral-700">
                                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M18 6L6 18M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
                            {manageError && (
                                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-md text-sm">
                                    {manageError}
                                </div>
                            )}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <div className="inline-flex rounded-lg border border-neutral-200 bg-neutral-50 overflow-hidden text-sm">
                                        {LANGS.map((lang) => (
                                            <button
                                                key={lang}
                                                type="button"
                                                onClick={() => setManageLang(lang)}
                                                className={`px-3 py-1.5 font-medium transition-colors ${manageLang === lang ? 'bg-white text-neutral-900 shadow-inner' : 'text-neutral-500 hover:text-neutral-800 hover:bg-neutral-100'}`}
                                            >
                                                {lang.toUpperCase()}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-xs font-medium text-neutral-600 mb-1">Titel ({manageLang.toUpperCase()})</label>
                                            <input
                                                type="text"
                                                value={editingCollage.titles[manageLang]}
                                                onChange={(e) => updateEditingValue('titles', manageLang, e.target.value)}
                                                className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                                                placeholder="Titel"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-neutral-600 mb-1">Beschreibung ({manageLang.toUpperCase()})</label>
                                            <textarea
                                                rows={4}
                                                value={editingCollage.descriptions[manageLang]}
                                                onChange={(e) => updateEditingValue('descriptions', manageLang, e.target.value)}
                                                className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                                                placeholder="Beschreibung"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-neutral-600 mb-1">Ordner</label>
                                            <select
                                                value={editingCollage.folderId}
                                                onChange={(e) => handleManageFolderChange(e.target.value)}
                                                className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                                            >
                                                <option value="">Kein Ordner</option>
                                                {folders.map((folder) => (
                                                    <option key={folder.id} value={folder.id}>
                                                        {folder.description_de || folder.description_en || folder.description_it || `Ordner ${folder.id}`}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-neutral-700">Collage-Bilder ({editingCollage.images.length})</span>
                                        <label className="inline-flex items-center px-3 py-2 rounded-lg bg-primary-50 text-primary-600 border border-primary-200 text-xs font-medium cursor-pointer hover:bg-primary-100">
                                            Neue Bilder
                                            <input type="file" accept="image/*" multiple className="hidden" onChange={handleAddImages} disabled={imageBusy} />
                                        </label>
                                    </div>
                                    <div className="space-y-3">
                                        {editingCollage.images.length === 0 ? (
                                            <div className="text-sm text-neutral-500 border border-dashed border-neutral-200 rounded-lg p-6 text-center">
                                                Keine Bilder vorhanden.
                                            </div>
                                        ) : (
                                            <div className="space-y-3">
                                                {editingCollage.images.map((image, index) => {
                                                    const imageUrl = getMediaUrl(image.filename);
                                                    const isCover = index === 0;
                                                    return (
                                                        <div key={image.id} className="flex items-center gap-4 border border-neutral-200 rounded-lg p-3">
                                                            <div className="w-24 h-16 bg-neutral-100 rounded overflow-hidden flex-shrink-0">
                                                                {imageUrl ? (
                                                                    <img src={imageUrl} alt={image.original_filename} className="w-full h-full object-cover" />
                                                                ) : (
                                                                    <div className="w-full h-full flex items-center justify-center text-neutral-400 text-xs">Kein Bild</div>
                                                                )}
                                                            </div>
                                                            <div className="flex-1 text-sm text-neutral-600">
                                                                <div className="font-medium text-neutral-800 truncate">{image.original_filename}</div>
                                                                <div className="text-xs text-neutral-500">{Math.round((image.file_size || 0) / 1024)} KB</div>
                                                                {isCover && (
                                                                    <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-primary-100 text-primary-600">Titelbild</span>
                                                                )}
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <button
                                                                    onClick={() => moveImage(index, -1)}
                                                                    disabled={index === 0 || imageBusy}
                                                                    className="p-2 rounded-full border border-neutral-200 text-neutral-500 hover:text-neutral-800 hover:border-neutral-300 disabled:opacity-40"
                                                                >
                                                                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                        <path d="M18 15l-6-6-6 6" />
                                                                    </svg>
                                                                </button>
                                                                <button
                                                                    onClick={() => moveImage(index, 1)}
                                                                    disabled={index === editingCollage.images.length - 1 || imageBusy}
                                                                    className="p-2 rounded-full border border-neutral-200 text-neutral-500 hover:text-neutral-800 hover:border-neutral-300 disabled:opacity-40"
                                                                >
                                                                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                        <path d="M6 9l6 6 6-6" />
                                                                    </svg>
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDeleteCollageImage(editingCollage.id, image.id)}
                                                                    className="p-2 rounded-full border border-red-200 text-red-600 hover:bg-red-50"
                                                                    disabled={imageBusy}
                                                                >
                                                                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                        <path d="M3 6h18" />
                                                                        <path d="M8 6v12a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V6" />
                                                                        <path d="M10 11v6" />
                                                                        <path d="M14 11v6" />
                                                                    </svg>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="border-t bg-neutral-50 px-6 py-4 flex items-center justify-between">
                            <div className="text-xs text-neutral-500">Die Reihenfolge bestimmt auch das Titelbild.</div>
                            <div className="flex gap-3">
                                <button
                                    onClick={closeManageModal}
                                    className="px-4 h-10 rounded-lg bg-neutral-200 hover:bg-neutral-300 text-neutral-800 text-sm font-medium"
                                >
                                    Abbrechen
                                </button>
                                <button
                                    disabled={savingChanges || imageBusy}
                                    onClick={handleSaveCollage}
                                    className="px-5 h-10 rounded-lg bg-primary-600 hover:bg-primary-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium inline-flex items-center gap-2"
                                >
                                    {(savingChanges || imageBusy) && <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></span>}
                                    Speichern
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CollageManager;
